import { Form, Input, Button, Row, Col, Space, Select } from "antd";
import {
  CopyOutlined,
  ReloadOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { Sparkles, Landmark } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { FetchBankAccounts_api } from "../../Api/Bank/bank_api";
import { PaymentTypeEnum } from "../../Constant";
import config from "../../Constant/config";
import { trimPriceAmount } from "../../lib/common";

const { Option } = Select;

export const BulkPaymentForm = ({
  maximumAmount,
  formData,
  isAdding,
  isLoading,
  handleSubmit,
  handleReset,
  copyToClipboard,
  handleFile,
  imageMetaData,
  payment_type,
}: any) => {
  const fields = [
    "slip_id",
    "transfer_id",
    "issued_date",
    "time",
    "amount",
    "slip_bank_name",
    "beneficiary_name",
    "beneficiary_bank_name",
    "branch_name",
    "account_number",
  ];

  const requiredFields = [
    "transfer_id",
    "issued_date",
    "amount",
    "slip_bank_name",
    "beneficiary_name",
    "bank_account_id",
  ];

  const amountValue = Form.useWatch("amount", formData);
  const bank_account_id = Form.useWatch("bank_account_id", formData);
  const from_bank_account_id = Form.useWatch("from_bank_account_id", formData);

  const { data: bankAccounts, isFetching: isFetchingBanks } = useQuery({
    queryKey: ["bankAccounts"],
    queryFn: async () => {
      const res = await FetchBankAccounts_api();
      return res?.data || [];
    },
    refetchOnWindowFocus: false,
  });

  return (
    <Form
      form={formData}
      layout="vertical"
      onFinish={(value) => handleSubmit(value)}
      initialValues={formData}
      className="p-4 rounded-lg"
    >
      <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 mb-6 flex flex-col gap-4">
        {/* Internal Account Selection */}
        <div className="flex items-start gap-3">
          <Landmark className="text-blue-500 w-5 h-5 mt-1" />
          <div className="flex-1">
            <Form.Item
              label={
                <span className="font-semibold text-blue-700">
                  {payment_type === PaymentTypeEnum.REMITTANCE
                    ? "Paid From (Internal Account)"
                    : "Deposited to (Internal Account)"}
                </span>
              }
              name="bank_account_id"
              rules={[
                {
                  required: true,
                  message: "Please select the internal bank account",
                },
              ]}
              className="mb-0"
            >
              <Select
                placeholder="-- Select Internal Account --"
                loading={isFetchingBanks}
                className="w-full"
              >
                {bankAccounts
                  ?.filter((acc: any) => acc.isOwn == 1)
                  .map((acc: any) => (
                    <Option key={acc.id} value={acc.id}>
                      {acc.account_name} - {acc.bank_name} ({acc.account_number}
                      )
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </div>
        </div>

        {/* External Account Selection - Only for Remittance */}
        {payment_type === PaymentTypeEnum.REMITTANCE && (
          <div className="flex items-start gap-3 border-t border-blue-200 pt-4">
            <Landmark className="text-orange-500 w-5 h-5 mt-1" />
            <div className="flex-1">
              <Form.Item
                label={
                  <span className="font-semibold text-orange-700">
                    Paid To (Beneficiary External Account)
                  </span>
                }
                name="from_bank_account_id" // Reusing this field to carry the External ID
                rules={[
                  {
                    required: true,
                    message: "Please select the beneficiary bank account",
                  },
                ]}
                className="mb-0"
              >
                <Select
                  placeholder="-- Select External Bank Account --"
                  loading={isFetchingBanks}
                  className="w-full"
                >
                  {bankAccounts
                    ?.filter((acc: any) => acc.isOwn == 0)
                    .map((acc: any) => (
                      <Option key={acc.id} value={acc.id}>
                        {acc.account_name} - {acc.bank_name} (
                        {acc.account_number})
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </div>
          </div>
        )}
      </div>

      <Row gutter={[16, 16]}>
        {fields.map((field: any) => {
          const isRequired = requiredFields.includes(field);
          const label = field.replaceAll("_", " ");
          const isAmount = field === "amount";
          const maximumAmountRule = isAmount
            ? [
                {
                  validator: (_: any, value: any) => {
                    if (value && Number(value) > maximumAmount) {
                      return Promise.reject(
                        `Amount cannot be greater than ${maximumAmount}`,
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]
            : [];

          return (
            <Col xs={24} md={12} key={field}>
              <Form.Item
                className="my-0"
                label={label}
                name={field}
                normalize={
                  isAmount
                    ? (value) =>
                        trimPriceAmount(
                          Number(value.toString().replace(/,/g, "")),
                        )
                    : undefined
                }
                rules={[
                  ...(isRequired
                    ? [
                        {
                          required: true,
                          message: `Please enter ${label}`,
                        },
                        ...maximumAmountRule,
                      ]
                    : []),
                  ...(field === "amount" &&
                  payment_type === PaymentTypeEnum.REMITTANCE
                    ? [
                        {
                          validator: (_: any, value: any) => {
                            if (
                              value &&
                              Number(value) > config.maxBulkPaymentLimit
                            ) {
                              return Promise.reject(
                                `Amount exceeds the maximum limit of ${config.maxBulkPaymentLimit}`,
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]
                    : []),
                ]}
              >
                <Input placeholder={`Enter ${label}`} />
              </Form.Item>
            </Col>
          );
        })}
      </Row>

      {/* Other IDs */}
      {formData?.other_ids?.length > 0 && (
        <Form.Item label="Other IDs" className="mt-5">
          <Space wrap>
            {formData.other_ids.map((id: any, idx: number) => (
              <Button
                key={idx}
                type="default"
                onClick={() => copyToClipboard(id)}
                icon={<CopyOutlined />}
              >
                {id}
              </Button>
            ))}
          </Space>
        </Form.Item>
      )}

      <Form.Item>
        <Space className="w-full justify-between flex mt-16">
          <Button
            loading={isLoading}
            className="flex items-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 via-cyan-500 to-sky-400 px-6 py-3 text-white font-semibold shadow-md transition-all duration-300 hover:from-cyan-500 hover:to-blue-600"
            htmlType="button"
            disabled={!imageMetaData}
            onClick={() => handleFile(imageMetaData)}
            icon={<Sparkles className="w-4 h-4" />}
          >
            Re analyze
          </Button>

          <div className="flex space-x-2">
            <Button
              disabled={isLoading}
              htmlType="button"
              onClick={handleReset}
              icon={<ReloadOutlined />}
            >
              Reset
            </Button>

            <Button
              disabled={
                isLoading ||
                maximumAmount === 0 ||
                (amountValue && Number(amountValue) > maximumAmount) ||
                !bank_account_id ||
                (payment_type === PaymentTypeEnum.REMITTANCE &&
                  !from_bank_account_id)
              }
              type="primary"
              htmlType="submit"
              loading={isAdding}
              icon={isAdding ? <LoadingOutlined /> : undefined}
            >
              Submit
            </Button>
          </div>
        </Space>
      </Form.Item>
    </Form>
  );
};
